/**
 * Ops-cost report create/edit: dynamic account checkboxes + Lux DB persistence.
 * Fetch intercept: lib/luxFetchHandlers/opsCostReport.js
 * Shared fetch helpers: lib/fetch/opsCostReport.js
 */
const LUX_OPS_COST_REPORT_API = '/api/impowerX/ops-cost-report'
const CHECKBOX_CONTAINER_ID = 'lux-ops-cost-report-account-checkboxes'
const OPEN_ITEMS_AS_OF_DATE_FIELD = 'openItemsAsOfDate'
const OPEN_RENT_ACCOUNTS_LABEL = 'Konten für Offene Posten'
const OPEN_RENT_ACCOUNTS_LIST_GRID_STYLE =
  'display: grid; grid-template-columns: repeat(4, max-content); column-gap: 24px; row-gap: 12px; width: 100%; margin: 0; padding: 0;'
const OPEN_RENT_BULK_ACTIONS_HTML = `
  <span class="lux-ops-cost-report-account-bulk" style="display: inline-flex; align-items: baseline; gap: 12px; margin-left: 16px; font-weight: 400;">
    <button type="button" class="ant-btn ant-btn-link lux-ops-cost-report-account-bulk-check" style="padding: 0; height: auto; line-height: 22px;">Alle anhaken</button>
    <button type="button" class="ant-btn ant-btn-link lux-ops-cost-report-account-bulk-uncheck" style="padding: 0; height: auto; line-height: 22px;">Alle abhaken</button>
  </span>
`
const CHECKBOX_REFRESH_DEBOUNCE_MS = 250

let luxOpsCostReportMountedRouteKey = null
let luxOpsCostReportCheckboxObserver = null
let luxOpsCostReportRefreshDebounceTimer = null
let luxOpsCostReportFlagsDirtySinceApiLoad = false
let luxOpsCostReportOpenItemsDateDirtySinceApiLoad = false

function getAuthToken() {
  try {
    const auth = JSON.parse(localStorage.getItem('auth') || '{}')
    return auth?.token || null
  } catch {
    return null
  }
}

function teardownOpsCostReportUi() {
  luxOpsCostReportCheckboxObserver?.disconnect()
  luxOpsCostReportCheckboxObserver = null
  if (luxOpsCostReportRefreshDebounceTimer != null) {
    clearTimeout(luxOpsCostReportRefreshDebounceTimer)
    luxOpsCostReportRefreshDebounceTimer = null
  }
  luxOpsCostReportEditorApi = null
  luxOpsCostReportMountedRouteKey = null
  luxOpsCostReportActiveSourceId = null
  rememberOpsCostReportPreviewContractId(null)
  luxOpsCostReportFetchState.lastOpenItemsAsOfDate = ''
  luxOpsCostReportFlagsDirtySinceApiLoad = false
  luxOpsCostReportOpenItemsDateDirtySinceApiLoad = false
  document.getElementById(CHECKBOX_CONTAINER_ID)?.remove()
}

function todayIsoDate() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function syncLastOpenItemsAsOfDateFromInput(input) {
  if (!input) return
  luxOpsCostReportFetchState.lastOpenItemsAsOfDate =
    (typeof input.getValue === 'function' ? input.getValue() : input.value)?.trim() ?? ''
}

function attachOpenItemsAsOfDateListeners(input) {
  if (!input || input.dataset.luxOpenItemsAsOfDateBound === '1') return
  input.dataset.luxOpenItemsAsOfDateBound = '1'
  const sync = () => {
    luxOpsCostReportOpenItemsDateDirtySinceApiLoad = true
    syncLastOpenItemsAsOfDateFromInput(input)
  }
  input.addEventListener('input', sync)
  input.addEventListener('change', sync)
  input.addEventListener('blur', sync)
}

function syncOpenItemsAsOfDateFromDom(root) {
  const scope = root || document.getElementById(CHECKBOX_CONTAINER_ID)
  const input = scope?.querySelector(`[data-field-name="${OPEN_ITEMS_AS_OF_DATE_FIELD}"] input`)
  if (!input) return
  syncLastOpenItemsAsOfDateFromInput(input)
}

function applyOpenItemsAsOfDateToDom(value) {
  const trimmed =
    value != null && typeof value === 'string' && value.trim() !== ''
      ? value.trim().slice(0, 10)
      : todayIsoDate()
  luxOpsCostReportFetchState.lastOpenItemsAsOfDate = trimmed
  const input = document.querySelector(`[data-field-name="${OPEN_ITEMS_AS_OF_DATE_FIELD}"] input`)
  if (input) {
    if (typeof input.setValue === 'function') input.setValue(trimmed)
    else input.value = trimmed
    syncLastOpenItemsAsOfDateFromInput(input)
  }
}

function styleOpenItemsAsOfDateField(dateField) {
  if (!dateField) return
  // Same footprint as Impower half-width fields (Briefpapier, Wärmekostenabrechnung, …).
  // Parent is ant-col-24, so without a cap the lux-picker stretches full width.
  dateField.style.minWidth = '0'
  dateField.style.width = '49%'
  dateField.style.maxWidth = '50%'
  dateField.style.boxSizing = 'border-box'
  dateField.style.marginBottom = '8px'
  const picker = dateField.querySelector('.lux-picker')
  if (picker) {
    picker.style.width = '100%'
    picker.style.maxWidth = '100%'
    picker.style.boxSizing = 'border-box'
  }
}

function initializeOpenItemsAsOfDateField(container) {
  if (!container) return

  const existing = container.querySelector(`[data-field-name="${OPEN_ITEMS_AS_OF_DATE_FIELD}"]`)
  if (existing) {
    styleOpenItemsAsOfDateField(existing)
    const input = existing.querySelector('input')
    attachOpenItemsAsOfDateListeners(input)
    if (
      luxOpsCostReportFetchState.lastOpenItemsAsOfDate &&
      input &&
      !(typeof input.getValue === 'function' ? input.getValue() : input.value)?.trim()
    ) {
      if (typeof input.setValue === 'function') {
        input.setValue(luxOpsCostReportFetchState.lastOpenItemsAsOfDate)
      } else {
        input.value = luxOpsCostReportFetchState.lastOpenItemsAsOfDate
      }
    }
    syncLastOpenItemsAsOfDateFromInput(input)
    return
  }

  const dateField = generateInputField({
    id: OPEN_ITEMS_AS_OF_DATE_FIELD,
    fieldType: 'DateInput',
    name: OPEN_ITEMS_AS_OF_DATE_FIELD,
    label: 'Offene Posten per',
    value: luxOpsCostReportFetchState.lastOpenItemsAsOfDate || todayIsoDate(),
    isDisabled: false,
  })
  dateField.classList.remove('ehv-element')
  dateField.dataset.fieldName = OPEN_ITEMS_AS_OF_DATE_FIELD
  styleOpenItemsAsOfDateField(dateField)
  const input = dateField.querySelector('input')
  input?.setAttribute('data-field-name', OPEN_ITEMS_AS_OF_DATE_FIELD)
  attachOpenItemsAsOfDateListeners(input)
  syncLastOpenItemsAsOfDateFromInput(input)

  const accountsBlock = container.querySelector('.lux-ops-cost-report-accounts')
  if (accountsBlock) {
    container.insertBefore(dateField, accountsBlock)
  } else {
    container.prepend(dateField)
  }
}

function readPropertyHrIdFromEditorForm() {
  const form = document.querySelector('.OpsCostReportEditorForm form')
  const hidden = form?.querySelector('input[name="propertyHrId"]')
  if (hidden?.value?.trim()) return hidden.value.trim()

  const link = document.querySelector('.OpsCostReportEditorForm a[href*="/properties/"]')
  const href = link?.getAttribute('href') ?? ''
  const match = href.match(/\/properties\/([^/?#]+)/)
  return match?.[1]?.trim() || null
}

async function fetchOpsCostReportMeta(reportId) {
  const token = getAuthToken()
  if (!token || !reportId) return null

  try {
    const doFetch = window.luxNativeFetch || window.fetch
    const res = await doFetch(
      `/services/pmp-accounting/api/v1/ops-cost-report/${encodeURIComponent(String(reportId))}`,
      {
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'same-origin',
      },
    )
    if (!res.ok) return null
    return await res.json()
  } catch {
    return null
  }
}

function initOpsCostReportEditorForm({ initialReportId }) {
  let reportId =
    initialReportId != null && Number.isFinite(initialReportId) && initialReportId > 0
      ? initialReportId
      : null
  let lastCategories = []
  let lastAccountInclusionFlags = {}
  let lastCategoryContextKey = null
  let lastRenderedHint = null
  let cachedPropertyHrId = null
  let categoriesLoadSeq = 0
  let categoriesLoadInFlight = false
  let domUpdateDepth = 0

  function setReportId(id) {
    if (Number.isFinite(id) && id > 0) reportId = id
  }

  function getCheckboxInput(id, root) {
    const escaped = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(id) : id.replace(/"/g, '\\"')
    return (root || document).querySelector(`[data-lux-account-flag="${escaped}"]`)
  }

  function syncFlagsFromDom(root) {
    const scope = root || document.getElementById(CHECKBOX_CONTAINER_ID)
    if (!scope) return
    for (const { id } of lastCategories) {
      const input = getCheckboxInput(id, scope)
      if (input) lastAccountInclusionFlags[id] = Boolean(input.checked)
    }
  }

  function applyFlagChecked(input, checked) {
    if (!input) return
    const checkedBool = Boolean(checked)
    if (input.checked === checkedBool) {
      const ant = input.closest('.ant-checkbox')
      const wrap = input.closest('.ant-checkbox-wrapper')
      const antOk = !ant || ant.classList.contains('ant-checkbox-checked') === checkedBool
      const wrapOk = !wrap || wrap.classList.contains('ant-checkbox-wrapper-checked') === checkedBool
      if (antOk && wrapOk) return
    }

    domUpdateDepth += 1
    try {
      input.checked = checkedBool
      const ant = input.closest('.ant-checkbox')
      const wrap = input.closest('.ant-checkbox-wrapper')
      ant?.classList.toggle('ant-checkbox-checked', checkedBool)
      wrap?.classList.toggle('ant-checkbox-wrapper-checked', checkedBool)
      wrap?.setAttribute('aria-checked', String(checkedBool))
    } finally {
      domUpdateDepth -= 1
    }
  }

  function mergeFlagsWithCategories(flags, categories) {
    const merged = Object.fromEntries(categories.map(({ id }) => [id, false]))
    if (!flags || typeof flags !== 'object') return merged
    for (const { id } of categories) {
      if (Object.prototype.hasOwnProperty.call(flags, id)) {
        merged[id] = Boolean(flags[id])
      }
    }
    return merged
  }

  function applyFlagsToDom(flags, root) {
    lastAccountInclusionFlags = mergeFlagsWithCategories(flags, lastCategories)
    for (const { id } of lastCategories) {
      const input = getCheckboxInput(id, root)
      if (!input) continue
      applyFlagChecked(input, lastAccountInclusionFlags[id])
    }
  }

  function categoryFlagSignature(categories) {
    return categories.map((c) => `${c.id}:${Boolean(lastAccountInclusionFlags[c.id])}`).join('|')
  }

  function setAllAccountFlags(checked) {
    if (lastCategories.length === 0) return
    const checkedBool = Boolean(checked)
    luxOpsCostReportFlagsDirtySinceApiLoad = true
    const container = document.getElementById(CHECKBOX_CONTAINER_ID)
    for (const { id } of lastCategories) {
      lastAccountInclusionFlags[id] = checkedBool
      const input = getCheckboxInput(id, container)
      applyFlagChecked(input, checkedBool)
      input?.closest('.ant-checkbox-wrapper')?.setAttribute('aria-checked', String(checkedBool))
    }
    const list = container?.querySelector('.lux-ops-cost-report-account-list')
    if (list) list.dataset.luxCategorySignature = categoryFlagSignature(lastCategories)
  }

  function bindAccountBulkActions(container) {
    const checkBtn = container?.querySelector('.lux-ops-cost-report-account-bulk-check')
    const uncheckBtn = container?.querySelector('.lux-ops-cost-report-account-bulk-uncheck')
    if (checkBtn && checkBtn.dataset.luxBulkBound !== '1') {
      checkBtn.dataset.luxBulkBound = '1'
      checkBtn.addEventListener('click', (e) => {
        e.preventDefault()
        e.stopPropagation()
        setAllAccountFlags(true)
      })
    }
    if (uncheckBtn && uncheckBtn.dataset.luxBulkBound !== '1') {
      uncheckBtn.dataset.luxBulkBound = '1'
      uncheckBtn.addEventListener('click', (e) => {
        e.preventDefault()
        e.stopPropagation()
        setAllAccountFlags(false)
      })
    }
  }

  function attachCheckboxListeners(wrapper, id) {
    if (!wrapper || wrapper.dataset.luxAccountFlagBound === '1') return
    wrapper.dataset.luxAccountFlagBound = '1'

    const input = getCheckboxInput(id, wrapper)
    if (!input) return
    const textEl = wrapper.querySelector('.lux-ops-cost-report-account-label')
    if (textEl) textEl.style.pointerEvents = 'auto'

    const setChecked = (checked) => {
      luxOpsCostReportFlagsDirtySinceApiLoad = true
      lastAccountInclusionFlags[id] = checked
      applyFlagChecked(input, checked)
      wrapper.setAttribute('aria-checked', String(checked))
    }

    const toggle = (e) => {
      e.preventDefault()
      e.stopPropagation()
      setChecked(!lastAccountInclusionFlags[id])
    }

    textEl?.addEventListener('click', toggle)
    wrapper.addEventListener('click', (e) => {
      if (e.target === textEl || textEl?.contains(e.target)) return
      toggle(e)
    })

    wrapper.addEventListener('keydown', (e) => {
      if (e.key === ' ' || e.key === 'Enter') toggle(e)
    })
  }

  function renderCategoryLoader(container, message = 'Konten werden geladen …') {
    const loaderKey = `__loading__:${message}`
    if (lastRenderedHint === loaderKey) return
    lastRenderedHint = loaderKey

    const list = container?.querySelector('.lux-ops-cost-report-account-list')
    if (!list) return

    domUpdateDepth += 1
    try {
      delete list.dataset.luxCategorySignature
      list.innerHTML = `
        <span class="lux-ops-cost-report-account-loading" style="display: inline-flex; align-items: center; gap: 8px; color: rgba(0,0,0,0.45); font-size: 14px;">
          <span class="ant-spin ant-spin-spinning" style="line-height: 1;">
            <span class="ant-spin-dot ant-spin-dot-spin">
              <i class="ant-spin-dot-item"></i>
              <i class="ant-spin-dot-item"></i>
              <i class="ant-spin-dot-item"></i>
              <i class="ant-spin-dot-item"></i>
            </span>
          </span>
          <span>${message}</span>
        </span>
      `
    } finally {
      domUpdateDepth -= 1
    }
  }

  function renderCategoryHint(container, message) {
    if (lastRenderedHint === message) return
    lastRenderedHint = message

    const list = container?.querySelector('.lux-ops-cost-report-account-list')
    if (!list) return

    domUpdateDepth += 1
    try {
      delete list.dataset.luxCategorySignature
      list.innerHTML = `<span class="lux-ops-cost-report-account-hint" style="color: rgba(0,0,0,0.45); font-size: 14px;">${message}</span>`
    } finally {
      domUpdateDepth -= 1
    }
  }

  function renderCategoryCheckboxes(container, categories) {
    const list = container?.querySelector('.lux-ops-cost-report-account-list')
    if (!list) return

    const signature = categoryFlagSignature(categories)
    if (list.dataset.luxCategorySignature === signature) return

    lastRenderedHint = null
    domUpdateDepth += 1
    try {
      list.innerHTML = ''
      list.dataset.luxCategorySignature = signature
      for (const { id, label } of categories) {
        const checked = Boolean(lastAccountInclusionFlags[id])
        const item = document.createElement('label')
        item.className = `ant-checkbox-wrapper${checked ? ' ant-checkbox-wrapper-checked' : ''}`
        item.style.flex = '0 1 auto'
        item.style.marginInline = '0'
        item.style.whiteSpace = 'nowrap'
        item.style.cursor = 'pointer'
        item.setAttribute('tabindex', '0')
        item.setAttribute('role', 'checkbox')
        item.setAttribute('aria-checked', String(checked))
        item.innerHTML = `
          <span class="ant-checkbox${checked ? ' ant-checkbox-checked' : ''}">
            <input type="checkbox" class="ant-checkbox-input" data-lux-account-flag="${id}"${checked ? ' checked' : ''}>
            <span class="ant-checkbox-inner"></span>
          </span>
          <span class="lux-ops-cost-report-account-label">${label}</span>
        `
        attachCheckboxListeners(item, id)
        list.appendChild(item)
      }
    } finally {
      domUpdateDepth -= 1
    }
  }

  function ensureCheckboxContainer(formRow) {
    let container = document.getElementById(CHECKBOX_CONTAINER_ID)
    if (container && !formRow.contains(container)) {
      container.remove()
      container = null
    }

    if (container) {
      const labelCol = container.querySelector('.lux-ops-cost-report-accounts .ant-form-item-label')
      if (labelCol) {
        labelCol.style.display = 'flex'
        labelCol.style.alignItems = 'baseline'
        labelCol.style.flexWrap = 'wrap'
        const titleSpan =
          labelCol.querySelector('.lux-ops-cost-report-accounts-title') ||
          labelCol.querySelector('label span')
        if (titleSpan) {
          titleSpan.classList.add('lux-ops-cost-report-accounts-title')
          titleSpan.textContent = OPEN_RENT_ACCOUNTS_LABEL
        }
        if (!container.querySelector('.lux-ops-cost-report-account-bulk')) {
          labelCol.insertAdjacentHTML('beforeend', OPEN_RENT_BULK_ACTIONS_HTML)
        }
      }
      const list = container.querySelector('.lux-ops-cost-report-account-list')
      if (list) list.style.cssText = OPEN_RENT_ACCOUNTS_LIST_GRID_STYLE
    }

    if (!container) {
      domUpdateDepth += 1
      try {
        container = document.createElement('div')
        container.id = CHECKBOX_CONTAINER_ID
        container.className = 'ant-col ant-col-24'
        container.style.paddingLeft = '16px'
        container.style.paddingRight = '16px'
        const sampleLabel = formRow.querySelector('.ant-form-item-label label')
        const labelTitleAttr = sampleLabel?.getAttribute('title') ?? ''

        container.innerHTML = `
          <div class="lux-ops-cost-report-accounts CheckboxInput" style="margin-top: 8px;">
            <div class="ant-row ant-form-item ant-form-item-with-help" style="row-gap: 0; margin-bottom: 0; flex-direction: column;">
              <div class="ant-col ant-form-item-label" style="flex: 0 0 auto; max-width: 100%; margin-left: 0; padding-left: 0; display: flex; align-items: baseline; flex-wrap: wrap;">
                <label title="${labelTitleAttr}"><span class="lux-ops-cost-report-accounts-title">${OPEN_RENT_ACCOUNTS_LABEL}</span></label>
                ${OPEN_RENT_BULK_ACTIONS_HTML}
              </div>
              <div class="ant-col ant-form-item-control" style="flex: 0 0 auto; max-width: 100%; margin-left: 0; padding-left: 0;">
                <div class="ant-form-item-control-input">
                  <div class="ant-form-item-control-input-content lux-ops-cost-report-account-list" style="${OPEN_RENT_ACCOUNTS_LIST_GRID_STYLE}"></div>
                </div>
              </div>
            </div>
          </div>
        `

        const contractTableCol = formRow.querySelector('.OpsCostReportContractSelectionTable')?.closest(
          '.ant-col',
        )
        if (contractTableCol) {
          formRow.insertBefore(container, contractTableCol)
        } else {
          formRow.appendChild(container)
        }
      } finally {
        domUpdateDepth -= 1
      }
    }

    initializeOpenItemsAsOfDateField(container)
    bindAccountBulkActions(container)

    return container
  }

  async function saveToLux(id) {
    const token = getAuthToken()
    if (!token || !id) return

    const container = document.getElementById(CHECKBOX_CONTAINER_ID)
    syncFlagsFromDom(container)
    syncOpenItemsAsOfDateFromDom(container)

    try {
      const doFetch = window.luxNativeFetch || window.fetch
      const res = await doFetch(LUX_OPS_COST_REPORT_API, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          reportId: id,
          accountInclusionFlags: lastAccountInclusionFlags,
          openItemsAsOfDate: luxOpsCostReportFetchState.lastOpenItemsAsOfDate.trim() || null,
        }),
      })
      if (!res.ok) {
        const detail = await res.text().catch(() => '')
        console.error('[ops-cost-report] save account flags failed:', res.status, detail)
        return
      }
      luxOpsCostReportFlagsDirtySinceApiLoad = false
      luxOpsCostReportOpenItemsDateDirtySinceApiLoad = false
      categoriesLoadSeq += 1
    } catch (err) {
      console.error('[ops-cost-report] save account flags failed:', err?.message || err)
    }
  }

  async function resolvePropertyHrId() {
    if (cachedPropertyHrId) return cachedPropertyHrId

    const fromForm = readPropertyHrIdFromEditorForm()
    if (fromForm) {
      cachedPropertyHrId = fromForm
      return fromForm
    }

    if (reportId) {
      const meta = await fetchOpsCostReportMeta(reportId)
      const fromReport = meta?.propertyHrId?.trim() || null
      if (fromReport) {
        cachedPropertyHrId = fromReport
        return fromReport
      }
    }

    return null
  }

  function refreshCheckboxUiFromMemory() {
    const formRow = document.querySelector(
      '.OpsCostReportEditorForm .section-content form .ant-row',
    )
    if (!formRow) return false

    const container = ensureCheckboxContainer(formRow)

    if (lastCategories.length === 0) return true

    const list = container?.querySelector('.lux-ops-cost-report-account-list')
    const hasCheckboxes = Boolean(list?.querySelector('[data-lux-account-flag]'))
    if (!hasCheckboxes) {
      renderCategoryCheckboxes(container, lastCategories)
    }
    applyFlagsToDom(lastAccountInclusionFlags, container)
    return true
  }

  async function loadCategoriesForCurrentContext() {
    if (categoriesLoadInFlight) return true

    const formRow = document.querySelector(
      '.OpsCostReportEditorForm .section-content form .ant-row',
    )
    if (!formRow) return false

    const container = ensureCheckboxContainer(formRow)
    const contextKeyPreview = cachedPropertyHrId || reportId || 'pending'
    if (!(lastCategoryContextKey === contextKeyPreview && lastCategories.length > 0)) {
      renderCategoryLoader(container)
    }

    const propertyHrId = await resolvePropertyHrId()

    if (!propertyHrId) {
      const hintKey = reportId ? 'hint:no-property:report' : 'hint:no-property:create'
      lastCategories = []
      lastCategoryContextKey = hintKey
      renderCategoryLoader(container, 'Objekt wird geladen …')
      return true
    }

    const contextKey = propertyHrId
    if (contextKey === lastCategoryContextKey && lastCategories.length > 0) {
      return refreshCheckboxUiFromMemory()
    }

    const token = getAuthToken()
    if (!token) return false

    renderCategoryLoader(container)

    const seq = ++categoriesLoadSeq
    categoriesLoadInFlight = true
    try {
      const doFetch = window.luxNativeFetch || window.fetch
      const query = reportId
        ? `reportId=${encodeURIComponent(String(reportId))}`
        : `propertyHrId=${encodeURIComponent(propertyHrId)}`
      const url = `${LUX_OPS_COST_REPORT_API}?${query}`
      const res = await doFetch(url, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'same-origin',
      })
      if (!res.ok) {
        lastCategoryContextKey = `hint:load-error:${contextKey}`
        renderCategoryHint(container, 'Konten konnten nicht geladen werden')
        return true
      }
      if (seq !== categoriesLoadSeq) {
        refreshCheckboxUiFromMemory()
        return true
      }

      const data = await res.json()
      const categories = Array.isArray(data?.categories) ? data.categories : []
      lastCategories = categories.map((c) => ({
        id: String(c.accountPattern),
        label: String(c.label ?? `${c.accountPattern} ${c.name}`),
      }))
      lastCategoryContextKey = contextKey
      if (!luxOpsCostReportFlagsDirtySinceApiLoad) {
        syncFlagsFromDom(container)
      }
      syncOpenItemsAsOfDateFromDom(container)
      if (luxOpsCostReportFlagsDirtySinceApiLoad) {
        lastAccountInclusionFlags = mergeFlagsWithCategories(lastAccountInclusionFlags, lastCategories)
      } else {
        lastAccountInclusionFlags = mergeFlagsWithCategories(data?.accountInclusionFlags, lastCategories)
        luxOpsCostReportFlagsDirtySinceApiLoad = false
      }
      if (!luxOpsCostReportOpenItemsDateDirtySinceApiLoad) {
        applyOpenItemsAsOfDateToDom(data?.openItemsAsOfDate)
        luxOpsCostReportOpenItemsDateDirtySinceApiLoad = false
      }

      if (lastCategories.length === 0) {
        renderCategoryHint(container, 'Keine offenen Posten-Konten für dieses Objekt')
        return true
      }

      renderCategoryCheckboxes(container, lastCategories)
      applyFlagsToDom(lastAccountInclusionFlags, container)

      return true
    } catch (err) {
      console.error('[ops-cost-report] load categories failed:', err?.message || err)
      lastCategoryContextKey = `hint:load-error:${contextKey}`
      renderCategoryHint(container, 'Konten konnten nicht geladen werden')
      return true
    } finally {
      categoriesLoadInFlight = false
    }
  }

  async function parseRequestBody(args) {
    const isStringUrl = typeof args[0] === 'string'
    let body = isStringUrl ? args[1]?.body : undefined
    if (body === undefined && !isStringUrl && args[0]?.body) {
      try {
        body = await args[0].clone().text()
      } catch {
        body = undefined
      }
    }
    if (body == null) return null
    if (typeof body === 'string') {
      try {
        return JSON.parse(body)
      } catch {
        return null
      }
    }
    if (!(body instanceof FormData) && !(body instanceof Blob)) return body
    return null
  }

  function parseReportIdFromBody(body) {
    if (!body || typeof body !== 'object') return null
    const id = Number(body.id)
    return Number.isFinite(id) && id > 0 ? id : null
  }

  async function handleOpsCostReportSave(origFetch, args) {
    const isStringUrl = typeof args[0] === 'string'
    const requestUrl = (isStringUrl ? args[0] : args[0]?.url) || ''
    const container = document.getElementById(CHECKBOX_CONTAINER_ID)
    syncFlagsFromDom(container)
    syncOpenItemsAsOfDateFromDom(container)

    const reqBody = await parseRequestBody(args)
    const propertyHrIdFromBody =
      reqBody && typeof reqBody.propertyHrId === 'string' ? reqBody.propertyHrId.trim() : ''
    if (propertyHrIdFromBody) {
      if (cachedPropertyHrId !== propertyHrIdFromBody) {
        cachedPropertyHrId = propertyHrIdFromBody
        lastCategoryContextKey = null
      }
    }
    const idFromRequest = parseReportIdFromBody(reqBody)
    const idFromUrl = parseReportIdFromUrl(requestUrl)
    const idFromPath = parseEditReportIdFromPathname()

    const response = await origFetch.apply(this, args)
    if (!response.ok) return response

    let idFromResponse = null
    if (response.status !== 204) {
      try {
        const data = await response.clone().json()
        const n = Number(data?.id)
        if (Number.isFinite(n) && n > 0) idFromResponse = n
      } catch {
        // empty or non-JSON body (e.g. 200 without payload)
      }
    }

    const resolvedId = idFromResponse ?? idFromRequest ?? idFromUrl ?? idFromPath
    if (resolvedId) {
      setReportId(resolvedId)
      luxOpsCostReportActiveSourceId = resolvedId
      try {
        await saveToLux(resolvedId)
        const root = document.getElementById(CHECKBOX_CONTAINER_ID)
        applyFlagsToDom(lastAccountInclusionFlags, root)
      } catch (err) {
        console.error('[ops-cost-report] after save persist failed:', err?.message || err)
      }
    }

    return response
  }

  function scheduleAccountCheckboxRefresh() {
    if (domUpdateDepth > 0) return
    if (luxOpsCostReportRefreshDebounceTimer != null) {
      clearTimeout(luxOpsCostReportRefreshDebounceTimer)
    }
    luxOpsCostReportRefreshDebounceTimer = setTimeout(() => {
      luxOpsCostReportRefreshDebounceTimer = null
      const fromForm = readPropertyHrIdFromEditorForm()
      if (fromForm && fromForm !== cachedPropertyHrId) {
        cachedPropertyHrId = fromForm
        lastCategoryContextKey = null
        luxOpsCostReportFlagsDirtySinceApiLoad = false
        luxOpsCostReportOpenItemsDateDirtySinceApiLoad = false
        void loadCategoriesForCurrentContext()
        return
      }
      refreshCheckboxUiFromMemory()
    }, CHECKBOX_REFRESH_DEBOUNCE_MS)
  }

  function connectCheckboxObserver() {
    const form = document.querySelector('.OpsCostReportEditorForm')
    if (!form) return

    if (luxOpsCostReportCheckboxObserver) {
      luxOpsCostReportCheckboxObserver.disconnect()
    }

    luxOpsCostReportCheckboxObserver = new MutationObserver((mutations) => {
      if (domUpdateDepth > 0) return
      const luxContainer = document.getElementById(CHECKBOX_CONTAINER_ID)
      if (
        luxContainer &&
        mutations.every((mutation) => {
          const target =
            mutation.target.nodeType === Node.ELEMENT_NODE
              ? mutation.target
              : mutation.target.parentElement
          return target != null && luxContainer.contains(target)
        })
      ) {
        return
      }
      scheduleAccountCheckboxRefresh()
    })
    luxOpsCostReportCheckboxObserver.observe(form, {
      childList: true,
      subtree: true,
    })
  }

  void loadCategoriesForCurrentContext()
  connectCheckboxObserver()

  return {
    isOpsCostReportSaveRequest,
    handleOpsCostReportSave,
    refreshAccountCheckboxes: scheduleAccountCheckboxRefresh,
  }
}

const PREVIEW_BUTTON_TEST_ID_RE = /^report-overview-table--row-(\d+)--preview-pdf$/

/** New BKA UI: the preview button's row key is the unitContractId. The render POST does not carry it. */
function installOpsCostReportPreviewClickCapture() {
  if (window.__luxOpsCostReportPreviewClickInstalled) return
  window.__luxOpsCostReportPreviewClickInstalled = true

  document.addEventListener(
    'click',
    (event) => {
      if (!isOpsCostReportPage(window.location.pathname)) return
      const target = event.target
      if (!(target instanceof Element)) return
      const button = target.closest('button[data-testid^="report-overview-table--row-"]')
      if (!button) return
      const testId = button.getAttribute('data-testid') || ''
      const fromTestId = testId.match(PREVIEW_BUTTON_TEST_ID_RE)
      if (!fromTestId) return
      const rowKey = button.closest('tr[data-row-key]')?.getAttribute('data-row-key')
      rememberOpsCostReportPreviewContractId(rowKey || fromTestId[1])
    },
    true,
  )
}

function onOpsCostReportPageMatch() {
  installOpsCostReportPreviewClickCapture()
  const routeKey = getUiRouteKey()
  if (!routeKey) return

  if (luxOpsCostReportMountedRouteKey !== routeKey) {
    teardownOpsCostReportUi()
    luxOpsCostReportMountedRouteKey = routeKey
    const sourceId = parseEditReportIdFromPathname()
    luxOpsCostReportEditorApi = initOpsCostReportEditorForm({
      initialReportId: sourceId,
    })
  }

  const sourceIdFromPath = parseEditReportIdFromPathname()
  if (sourceIdFromPath) {
    luxOpsCostReportActiveSourceId = sourceIdFromPath
  }
}

; (function installOpsCostReportRouteTeardownHook() {
  if (window.__luxOpsCostReportRouteTeardownInstalled) return
  window.__luxOpsCostReportRouteTeardownInstalled = true

  function maybeTeardownOpsCostReportUi() {
    if (isOpsCostReportPage(window.location.pathname)) return
    if (luxOpsCostReportMountedRouteKey != null) teardownOpsCostReportUi()
  }

  window.addEventListener('popstate', maybeTeardownOpsCostReportUi)

  for (const methodName of ['pushState', 'replaceState']) {
    const original = history[methodName].bind(history)
    history[methodName] = function (...args) {
      const result = original(...args)
      maybeTeardownOpsCostReportUi()
      return result
    }
  }
})()
